import React, { useState, useEffect } from 'react';
import { 
  Sparkles, 
  Bot, 
  Send, 
  Settings, 
  Zap, 
  RefreshCw,
  Copy,
  ArrowRight,
  Key,
  X,
  AlertCircle
} from 'lucide-react';
import {
  initializeOpenAI,
  isOpenAIInitialized
} from '../services/openai';
import {
  enhancePromptSecure,
  generateVariationsSecure,
  convertPromptFormatSecure,
  suggestImprovementsSecure
} from '../services/openaiSecure';
import { useApiKeys } from '../contexts/ApiKeyContext';
import { useAuth } from '../contexts/AuthContext';
import aiService from '../services/aiService';
import { openAIProxy, isProxyAvailable } from '../services/api/aiProxy';

interface AIAssistantProps {
  currentPrompt?: string;
  platform?: string;
  onPromptUpdate?: (prompt: string) => void;
}

const AIAssistant: React.FC<AIAssistantProps> = ({ 
  currentPrompt = '', 
  platform = 'midjourney',
  onPromptUpdate 
}) => {
  const { getApiKeyForModel, isKeyConfigured } = useApiKeys();
  const { profile } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [showApiKeyInput, setShowApiKeyInput] = useState(false);
  const [input, setInput] = useState(currentPrompt);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<string | string[]>('');
  const [activeTab, setActiveTab] = useState<'enhance' | 'variations' | 'convert' | 'improve'>('enhance');
  const [selectedModel, setSelectedModel] = useState<'gpt4' | 'claude' | 'gemini'>('gpt4');
  
  // Check if user is Pro or has API keys configured
  const isPro = profile && ['pro', 'premium', 'enterprise'].includes(profile.subscription || 'free');
  const openAIKey = getApiKeyForModel('gpt4');
  const claudeKey = getApiKeyForModel('claude');
  const geminiKey = getApiKeyForModel('gemini');
  const hasAnyKey = isPro || !!(openAIKey || claudeKey || geminiKey);

  useEffect(() => {
    // Initialize OpenAI if we have a key
    if (openAIKey && !openAIKey.includes('SYSTEM')) {
      initializeOpenAI(openAIKey);
    }
  }, [openAIKey]);

  const getAvailableModel = () => {
    if (selectedModel === 'gpt4' && openAIKey) return 'gpt4';
    if (selectedModel === 'claude' && claudeKey) return 'claude';
    if (selectedModel === 'gemini' && geminiKey) return 'gemini';
    // Fallback to any available model
    if (openAIKey) return 'gpt4';
    if (claudeKey) return 'claude';
    if (geminiKey) return 'gemini';
    return null;
  };

  const handleEnhance = async () => {
    if (!input) return;
    const model = getAvailableModel();
    if (!model) {
      setResult('No API key configured. Please add an API key in settings.');
      return;
    }
    
    setLoading(true);
    try {
      // Use secure API for GPT-4 with Pro users
      if (model === 'gpt4' && isPro) {
        const enhanced = await enhancePromptSecure(input, platform);
        setResult(enhanced);
        if (onPromptUpdate) {
          onPromptUpdate(enhanced);
        }
      } else {
        const apiKey = model === 'gpt4' ? openAIKey : model === 'claude' ? claudeKey : geminiKey;
        const response = await aiService.enhancePrompt(input, model, apiKey);
        
        if (response.success && response.data) {
          setResult(response.data.enhanced);
          if (onPromptUpdate) {
            onPromptUpdate(response.data.enhanced);
          }
        } else {
          setResult(response.error || 'Error enhancing prompt.');
        }
      }
    } catch (error) {
      setResult('Error enhancing prompt. Please check your API key.');
    }
    setLoading(false);
  };

  const handleVariations = async () => {
    if (!input) return;
    
    // Use OpenAI for variations if available
    if (openAIKey) {
      setLoading(true);
      try {
        const apiKeyToUse = isPro ? undefined : openAIKey;
        const variations = await generateVariationsSecure(input, 3, apiKeyToUse);
        setResult(variations);
      } catch (error) {
        setResult('Error generating variations.');
      }
      setLoading(false);
    } else {
      setResult('OpenAI API key required for variations.');
    }
  };

  const handleConvert = async (toPlatform: string) => {
    if (!input) return;
    
    if (openAIKey) {
      setLoading(true);
      try {
        const apiKeyToUse = isPro ? undefined : openAIKey;
        const converted = await convertPromptFormatSecure(input, platform, toPlatform, apiKeyToUse);
        setResult(converted);
        if (onPromptUpdate) {
          onPromptUpdate(converted);
        }
      } catch (error) {
        setResult('Error converting prompt.');
      }
      setLoading(false);
    } else {
      setResult('OpenAI API key required for format conversion.');
    }
  };

  const handleImprove = async () => {
    if (!input) return;
    
    if (openAIKey) {
      setLoading(true);
      try {
        const apiKeyToUse = isPro ? undefined : openAIKey;
        const suggestions = await suggestImprovementsSecure(input, apiKeyToUse);
        setResult(suggestions);
      } catch (error) {
        setResult('Error getting suggestions. Please check your API key.');
      }
      setLoading(false);
    } else {
      setResult('OpenAI API key required for improvement suggestions.');
    }
  };

  const copyResult = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const applyPrompt = (prompt: string) => {
    if (onPromptUpdate) {
      onPromptUpdate(prompt);
    }
  };

  React.useEffect(() => {
    setInput(currentPrompt);
  }, [currentPrompt]);

  React.useEffect(() => {
    const configured = localStorage.getItem('openai_configured');
    if (configured === 'true') {
      setShowApiKeyInput(false);
    }
  }, []);

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 p-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-full shadow-2xl hover:shadow-3xl transform hover:scale-110 transition-all z-40 group"
      >
        <Bot className="w-6 h-6 group-hover:rotate-12 transition-transform" />
        <span className="absolute -top-8 right-0 bg-black text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
          AI Assistant
        </span>
      </button>

      {/* AI Assistant Panel */}
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-end fade-in">
          <div className="bg-white w-full max-w-2xl h-full overflow-y-auto shadow-2xl slide-in">
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-6">
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-3">
                  <Bot className="w-8 h-8" />
                  <h2 className="text-2xl font-bold">AI Prompt Assistant</h2>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-sm opacity-90">
                Powered by AI models to help you create amazing prompts
              </p>
            </div>

            {/* API Status and Model Selection */}
            <div className="p-4 bg-gray-50 border-b">
              {hasAnyKey ? (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Key className="w-5 h-5 text-green-600" />
                      <span className="text-sm font-medium text-gray-700">AI Models Available</span>
                    </div>
                    <select
                      value={selectedModel}
                      onChange={(e) => setSelectedModel(e.target.value as 'gpt4' | 'claude' | 'gemini')}
                      className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {openAIKey && <option value="gpt4">GPT-4</option>}
                      {claudeKey && <option value="claude">Claude 3</option>}
                      {geminiKey && <option value="gemini">Gemini Pro</option>}
                    </select>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {openAIKey && (
                      <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                        ✓ OpenAI
                      </span>
                    )}
                    {claudeKey && (
                      <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">
                        ✓ Claude
                      </span>
                    )}
                    {geminiKey && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                        ✓ Gemini
                      </span>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">No API Keys Configured</p>
                    <p className="text-xs text-gray-600 mt-1">
                      Please add API keys in the settings (key icon in header) to use AI features.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Main Content */}
            <div className="p-6">
              {/* Input Area */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Prompt
                </label>
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Enter your prompt here..."
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
              </div>

              {/* Action Tabs */}
              <div className="flex gap-2 mb-6 overflow-x-auto">
                <button
                  onClick={() => setActiveTab('enhance')}
                  className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-all ${
                    activeTab === 'enhance'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Zap className="w-4 h-4 inline mr-1" />
                  Enhance
                </button>
                <button
                  onClick={() => setActiveTab('variations')}
                  className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-all ${
                    activeTab === 'variations'
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <RefreshCw className="w-4 h-4 inline mr-1" />
                  Variations
                </button>
                <button
                  onClick={() => setActiveTab('convert')}
                  className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-all ${
                    activeTab === 'convert'
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <ArrowRight className="w-4 h-4 inline mr-1" />
                  Convert
                </button>
                <button
                  onClick={() => setActiveTab('improve')}
                  className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-all ${
                    activeTab === 'improve'
                      ? 'bg-orange-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Sparkles className="w-4 h-4 inline mr-1" />
                  Improve
                </button>
              </div>

              {/* Action Content */}
              <div className="mb-6">
                {activeTab === 'enhance' && (
                  <button
                    onClick={handleEnhance}
                    disabled={loading || !input}
                    className="w-full py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
                  >
                    {loading ? 'Enhancing...' : 'Enhance Prompt with AI'}
                  </button>
                )}

                {activeTab === 'variations' && (
                  <button
                    onClick={handleVariations}
                    disabled={loading || !input}
                    className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
                  >
                    {loading ? 'Generating...' : 'Generate 5 Variations'}
                  </button>
                )}

                {activeTab === 'convert' && (
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => handleConvert('dall-e')}
                      disabled={loading || !input}
                      className="py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors disabled:opacity-50"
                    >
                      To DALL-E
                    </button>
                    <button
                      onClick={() => handleConvert('stable-diffusion')}
                      disabled={loading || !input}
                      className="py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors disabled:opacity-50"
                    >
                      To Stable Diffusion
                    </button>
                  </div>
                )}

                {activeTab === 'improve' && (
                  <button
                    onClick={handleImprove}
                    disabled={loading || !input}
                    className="w-full py-3 bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
                  >
                    {loading ? 'Analyzing...' : 'Get Improvement Suggestions'}
                  </button>
                )}
              </div>

              {/* Results */}
              {result && (
                <div className="space-y-3">
                  <h3 className="font-semibold text-gray-900">Results:</h3>
                  {Array.isArray(result) ? (
                    <div className="space-y-3">
                      {result.map((item, index) => (
                        <div
                          key={index}
                          className="p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-blue-300 transition-colors"
                        >
                          <p className="text-sm text-gray-800 mb-2">{item}</p>
                          <div className="flex gap-2">
                            <button
                              onClick={() => copyResult(item)}
                              className="text-xs px-3 py-1 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                            >
                              <Copy className="w-3 h-3 inline mr-1" />
                              Copy
                            </button>
                            <button
                              onClick={() => applyPrompt(item)}
                              className="text-xs px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                            >
                              Use This
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-sm text-gray-800 mb-2">{result}</p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => copyResult(result)}
                          className="text-xs px-3 py-1 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                        >
                          <Copy className="w-3 h-3 inline mr-1" />
                          Copy
                        </button>
                        <button
                          onClick={() => applyPrompt(result)}
                          className="text-xs px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                        >
                          Use This
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AIAssistant;